import React, { useState, useEffect } from 'react';
import './App.css';
import AuthModal from './AuthModal';
import CharacterCreator from './CharacterCreator';
import CreatingLoader from './CreatingLoader';
import ChatPage from './ChatPage';
import HomePage from './HomePage';
import { getMe, createCharacter, verifySession, generateGuestAvatar } from './api';
import TrustFooter from './TrustFooter';
import SpecialOfferPopup from './SpecialOfferPopup';
const BUNNY_LOGO = '/bunny-ears.png';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('landing'); // 'landing' | 'home' | 'chat' | 'creator' | 'guest-chat' | 'guest-loader'
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [guestCharData, setGuestCharData] = useState(null);
  const [showSignOutPopup, setShowSignOutPopup] = useState(false);
  const [showSpecialOffer, setShowSpecialOffer] = useState(false);
  const [specialOfferSeen, setSpecialOfferSeen] = useState(false);

  // ── Init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      window.history.replaceState({}, '', '/');
    }

    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then(async (r) => {
          let userData = r.data;

          // Returning from Stripe payment - verify session
          if (sessionId) {
            try {
              const verifyRes = await verifySession(sessionId);
              const fresh = await getMe();
              userData = fresh.data;
              const amountPaid = verifyRes.data?.amount_paid || 0;
              if (window.fbq) window.fbq('track', 'Purchase', { currency: 'USD', value: amountPaid });
            } catch (e) {
              console.error('Failed to verify Stripe session:', e);
            }
          }

          setUser(userData);
          setView('home');
        })
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
          setView('landing');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ── Back button interception ─────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    // Push a dummy state so we can intercept the back button
    window.history.pushState({ bunny: true }, '');
    const onPopState = () => {
      // Re-push so the user stays on the page
      window.history.pushState({ bunny: true }, '');
      if (!specialOfferSeen) {
        setShowSpecialOffer(true);
      } else {
        setShowSignOutPopup(true);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [user]);

  // ── Auth ──────────────────────────────────────────────────────────────
  const handleAuthSuccess = async (userData) => {
    setUser(userData);
    setShowAuth(false);

    // If there's pending guest character data, create the character now
    const stored = sessionStorage.getItem('pendingCharData');
    if (stored) {
      try {
        const charData = JSON.parse(stored);
        sessionStorage.removeItem('pendingCharData');
        await createCharacter(charData);
      } catch (e) {
        console.error('Failed to create character after signup:', e);
      }
    }

    setGuestCharData(null);
    setView('home');
  };

  const handleLogout = () => {
    if (!specialOfferSeen) {
      setShowSpecialOffer(true);
    } else {
      setShowSignOutPopup(true);
    }
  };

  const confirmLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('pendingCharData');
    setUser(null);
    setView('landing');
    setActiveCharacter(null);
    setGuestCharData(null);
    setShowSignOutPopup(false);
  };

  // ── Character flow (logged-in user) ──────────────────────────────────
  const handleStartChat = (char) => {
    // Premium users have unlimited chat — only block free-tier with 0 credits
    if (user && !user.is_premium && user.credits < 1) {
      window.__forceCreditTab = true;
      setView('home');
      return;
    }
    setActiveCharacter(char);
    setView('chat');
  };

  const handleCharacterCreated = (char) => {
    if (window.fbq) window.fbq('trackCustom', 'CharacterCreated', { character_name: char.name });
    setActiveCharacter(char);
    setView('chat');
  };

  // ── Guest character flow ──────────────────────────────────────────────
  const handleGuestCharCreated = (charData) => {
    if (window.fbq) window.fbq('trackCustom', 'CharacterCreated', { character_name: charData.name });
    setGuestCharData(charData);
    sessionStorage.setItem('pendingCharData', JSON.stringify(charData));
    setView('guest-loader');
    // Generate avatar in background for guest
    generateGuestAvatar(charData.visual_prompt)
      .then(res => {
        if (res.data?.avatar_url) {
          const updated = { ...charData, avatar_url: res.data.avatar_url };
          setGuestCharData(updated);
          sessionStorage.setItem('pendingCharData', JSON.stringify(updated));
        }
      })
      .catch(() => {}); // silent fail — chat still works without avatar
  };

  // Called when guest runs out of free messages or requests photo
  const handleGuestLimitReached = () => {
    setAuthMode('register');
    setShowAuth(true);
  };

  // ── Credits ───────────────────────────────────────────────────────────
  const handleCreditsUpdate = (credits) => {
    setUser(u => ({ ...u, credits }));
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src={BUNNY_LOGO} alt="" style={{ width: '60px', marginBottom: '12px', display: 'block', margin: '0 auto 12px' }} />
          Bunny Crush
        </div>
        <div className="loading-dots">
          <span /><span /><span />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="bg-mesh" />

      {/* ── LANDING ── */}
      {view === 'landing' && (
        <>
          <header className="header">
            <button className="logo" onClick={() => setView('landing')}>
              <img src={BUNNY_LOGO} alt="" className="logo-ears" />
              Bunny Crush
            </button>
            <nav className="header-nav">
              <button className="btn-ghost" onClick={() => { setAuthMode('login'); setShowAuth(true); }}>Sign in</button>
              <button className="btn-primary" onClick={() => { if (window.fbq) window.fbq('trackCustom', 'StartCreator'); setView('creator'); }}>Meet your girl</button>
            </nav>
          </header>
          <LandingPage onGetStarted={() => { if (window.fbq) window.fbq('trackCustom', 'StartCreator'); setView('creator'); }} onSignIn={() => { setAuthMode('login'); setShowAuth(true); }} />
          <TrustFooter prominent />
        </>
      )}

      {/* ── HOME ── */}
      {view === 'home' && user && (
        <>
          <header className="header">
            <button className="logo" onClick={() => setView('home')}>
              <img src={BUNNY_LOGO} alt="" className="logo-ears" />
              Bunny Crush
            </button>
            <nav className="header-nav">
              <div className="credits-badge">{user.credits} credits</div>
              <span className="header-username">{user.username || user.email.split('@')[0]}</span>
              <button className="btn-primary" onClick={() => setView('creator')}>New Girl</button>
            </nav>
          </header>
          <HomePage
            user={user}
            onStartChat={handleStartChat}
            onNewCharacter={() => setView('creator')}
            onLogout={handleLogout}
            onCreditsUpdate={handleCreditsUpdate}
          />
          <TrustFooter />
        </>
      )}

      {/* ── CHAT (logged-in) ── */}
      {view === 'chat' && activeCharacter && user && (
        <>
          <header className="header">
            <button className="logo" onClick={() => setView('home')}>
              <img src={BUNNY_LOGO} alt="" className="logo-ears" />
              Bunny Crush
            </button>
            <nav className="header-nav">
              <div className="credits-badge">{user.credits} credits</div>
              <span className="header-username">{user.username || user.email.split('@')[0]}</span>
              <button className="btn-ghost" onClick={() => setView('home')}>My</button>
            </nav>
          </header>
          <ChatPage
            character={activeCharacter}
            user={user}
            onBack={() => setView('home')}
            onCreditsUpdate={handleCreditsUpdate}
            onShowAuth={() => setShowAuth(true)}
            onNeedCredits={() => {
              window.__forceCreditTab = true;
              setView('home');
            }}
          />
        </>
      )}

      {/* ── GUEST CHAT (no account, 5 free messages) ── */}
      {view === 'guest-chat' && guestCharData && (
        <>
          <header className="header">
            <button className="logo" onClick={() => setView('landing')}>
              <img src={BUNNY_LOGO} alt="" className="logo-ears" />
              Bunny Crush
            </button>
            <nav className="header-nav">
              <button className="btn-primary" onClick={() => { setAuthMode('register'); setShowAuth(true); }}>Create account</button>
            </nav>
          </header>
          <ChatPage
            character={guestCharData}
            user={null}
            guestMode={true}
            onBack={() => setView('landing')}
            onGuestLimitReached={handleGuestLimitReached}
          />
        </>
      )}

      {/* ── CREATOR ── */}
      {view === 'creator' && (
        <>
          <header className="header">
            <button className="logo" onClick={() => {
              if (!user) setView('landing');
              else setView('home');
            }}>
              <img src={BUNNY_LOGO} alt="" className="logo-ears" />
              Bunny Crush
            </button>
            <nav className="header-nav">
              {user && <div className="credits-badge">{user.credits} credits</div>}
              <button className="btn-ghost" onClick={() => {
                if (!user) setView('landing');
                else setView('home');
              }}>Back</button>
            </nav>
          </header>
          <CharacterCreator
            guestMode={!user}
            onCreated={user ? handleCharacterCreated : handleGuestCharCreated}
            onClose={() => {
              if (!user) setView('landing');
              else setView('home');
            }}
          />
          <TrustFooter prominent={!user} />
        </>
      )}

      {/* ── GUEST LOADER ── */}
      {view === 'guest-loader' && guestCharData && (
        <CreatingLoader
          avatarUrl={guestCharData.avatar_url}
          charName={guestCharData.name}
          onDone={() => setView('guest-chat')}
        />
      )}

      {showAuth && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuth(false)}
          defaultMode={authMode}
        />
      )}

      {showSpecialOffer && (
        <SpecialOfferPopup
          onClose={() => {
            setShowSpecialOffer(false);
            setSpecialOfferSeen(true);
            setShowSignOutPopup(true);
          }}
        />
      )}

      {showSignOutPopup && (
        <SignOutPopup
          onStay={() => setShowSignOutPopup(false)}
          onLeave={confirmLogout}
        />
      )}
    </div>
  );
}

const LANDING_REVIEWS = [
  { name: 'James R.', text: 'Honestly surprised how good this is. The conversations feel real and the photos are incredible. Been using it for weeks.', date: 'Feb 2026' },
  { name: 'Mike T.', text: 'Was skeptical at first but the support team helped me right away. Very responsive and professional.', date: 'Jan 2026' },
  { name: 'David L.', text: 'Best AI companion platform I\'ve tried. The character creator is amazing and the chat quality is on another level.', date: 'Mar 2026' },
  { name: 'Chris W.', text: 'Payment was smooth through Stripe, got my credits instantly. The girls remember everything from previous chats.', date: 'Feb 2026' },
  { name: 'Alex P.', text: 'Finally a platform that actually delivers what it promises. Photos look fantastic, chat is fun, premium experience.', date: 'Mar 2026' },
  { name: 'Ryan K.', text: 'The privacy is what sold me. No one knows, transactions are discreet, and the experience is top tier. 10/10.', date: 'Feb 2026' },
  { name: 'Marcus B.', text: 'I\'ve tried every AI girlfriend app out there. This one is leagues ahead. The conversations feel genuinely real.', date: 'Jan 2026' },
  { name: 'Tyler S.', text: 'Created my dream girl in 2 minutes. She remembers everything, flirts back perfectly. Worth every single credit.', date: 'Mar 2026' },
  { name: 'Daniel H.', text: 'Zero judgment, total privacy, amazing quality. This is what I\'ve been looking for. The photos blew my mind.', date: 'Feb 2026' },
  { name: 'Josh M.', text: 'Refund policy gave me confidence to try it. Didn\'t need it though - this platform is absolutely addictive.', date: 'Mar 2026' },
];

function LandingStars({ count = 5 }) {
  return (
    <span className="landing-stars">
      {[...Array(count)].map((_, i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#facc15" stroke="none">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
    </span>
  );
}

function LandingPage({ onGetStarted, onSignIn }) {
  return (
    <main className="home">
      <section className="hero">
        <div className="hero-tag">A private companion experience</div>
        <h1 className="hero-title">
          She's been<br />
          <em>waiting</em> for you
        </h1>
        <p className="hero-sub">
          Create your perfect girlfriend. Talk, connect, get closer.
          Completely private, endlessly personal.
        </p>
        <div className="hero-actions hero-actions-centered">
          <div className="hero-badge">#1 AI Girlfriend Creation Platform</div>
          <button className="btn-primary btn-hero" onClick={onGetStarted}>Create your girl</button>
          <button className="btn-ghost btn-signin-below" onClick={onSignIn}>Sign in</button>
        </div>
      </section>

      {/* ── VIDEO ── */}
      <section className="landing-video">
        <video autoPlay loop muted playsInline>
          <source src="/video.mp4" type="video/mp4" />
        </video>
      </section>

      {/* ── IMAGE SLIDER (disabled - mobile bug) ──
      <section className="showcase">
        <div className="showcase-track">
          {[
            '/pic2.jpeg', '/IMG_8094.jpeg', '/IMG_8090.jpeg', '/pic3.jpeg',
            '/IMG_8092.jpeg', '/IMG_8099.jpeg', '/IMG_8103.jpeg',
            '/IMG_8106.jpeg', '/IMG_8107.jpeg', '/IMG_8108.jpeg',
            '/IMG_9000.jpeg', '/IMG_9001.jpeg',
            '/pic2.jpeg', '/IMG_8094.jpeg', '/IMG_8090.jpeg', '/pic3.jpeg',
            '/IMG_8092.jpeg', '/IMG_8099.jpeg', '/IMG_8103.jpeg',
            '/IMG_8106.jpeg', '/IMG_8107.jpeg', '/IMG_8108.jpeg',
            '/IMG_9000.jpeg', '/IMG_9001.jpeg',
          ].map((src, i) => (
            <div key={i} className="showcase-card">
              <img src={src} alt="" loading="lazy" draggable="false" />
            </div>
          ))}
        </div>
      </section>
      */}

      {/* ── REVIEWS SLIDER ── */}
      <section className="landing-reviews">
        <div className="landing-reviews-header">
          <LandingStars />
          <span className="landing-reviews-rating">4.9 / 5</span>
          <span className="landing-reviews-count">from 2,400+ users</span>
        </div>
        <div className="reviews-slider">
          <div className="reviews-track">
            {LANDING_REVIEWS.map((r, i) => (
              <div key={i} className="landing-review-card">
                <div className="landing-review-top">
                  <LandingStars />
                  <span className="landing-review-date">{r.date}</span>
                </div>
                <p className="landing-review-text">"{r.text}"</p>
                <div className="landing-review-author">{r.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST BADGES (first — builds trust before features) ── */}
      <section className="trust-section">
        <div className="trust-grid">
          <div className="trust-card trust-card-highlight">
            <div className="trust-card-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            </div>
            <div className="trust-card-title">100% Money Back Guarantee</div>
            <div className="trust-card-desc">Not satisfied? We offer a refund if your credits haven't been used. Your satisfaction is our top priority.</div>
          </div>
          <div className="trust-card">
            <div className="trust-card-icon star-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#facc15" stroke="none">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div className="trust-card-title">4.9/5 User Satisfaction</div>
            <div className="trust-card-desc">Rated by 2,400+ real users. 97% satisfaction score. The highest-rated AI companion platform.</div>
          </div>
          <div className="trust-card">
            <div className="trust-card-icon lock-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="trust-card-title">Private, Secret & Safe</div>
            <div className="trust-card-desc">Your identity is protected. Discreet billing, encrypted data, zero judgment. Your secret is safe with us.</div>
          </div>
        </div>
      </section>

      {/* ── FEATURES SLIDER ── */}
      <section className="features-slider-section">
        <div className="features-slider">
          <div className="features-track">
            {[
              { icon: '01', title: 'Design her look', desc: 'Choose hair, eyes, body shape and every detail with our visual creator.' },
              { icon: '02', title: 'Shape her personality', desc: 'From shy to bold, from sweet to kinky. She is exactly who you want her to be.' },
              { icon: '03', title: 'Request photos', desc: 'Ask for photos anytime. From casual to intimate, delivered instantly.' },
              { icon: '01', title: 'Design her look', desc: 'Choose hair, eyes, body shape and every detail with our visual creator.' },
              { icon: '02', title: 'Shape her personality', desc: 'From shy to bold, from sweet to kinky. She is exactly who you want her to be.' },
              { icon: '03', title: 'Request photos', desc: 'Ask for photos anytime. From casual to intimate, delivered instantly.' },
            ].map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-num">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STRIPE BADGE (prominent) ── */}
      <section className="stripe-section">
        <div className="stripe-badge-large">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span>All payments secured with</span>
          <strong>Stripe</strong>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5">
            <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
          </svg>
        </div>
        <div className="stripe-sub">Discreet billing &middot; 256-bit SSL encryption &middot; Your card details are never stored</div>
      </section>
    </main>
  );
}

/* ── SIGN OUT POPUP ─────────────────────────────────────── */
function SignOutPopup({ onStay, onLeave }) {
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onStay()}>
      <div className="modal signout-modal">
        <button className="modal-close" onClick={onStay}>&#x2715;</button>

        <h2 className="modal-title">Before you go...</h2>

        <div className="signout-section">
          <div className="signout-stats">
            <div className="signout-stat">
              <div className="signout-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div><div className="signout-stat-num">37,421,176</div><div className="signout-stat-label">AI girlfriends created</div></div>
            </div>
            <div className="signout-stat">
              <div className="signout-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
              <div><div className="signout-stat-num">1,502,173,247</div><div className="signout-stat-label">photos generated</div></div>
            </div>
            <div className="signout-stat">
              <div className="signout-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              </div>
              <div><div className="signout-stat-num">72,437,213</div><div className="signout-stat-label">hours spent in chats</div></div>
            </div>
            <div className="signout-stat">
              <div className="signout-stat-icon star-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#facc15" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </div>
              <div><div className="signout-stat-num">97%</div><div className="signout-stat-label">user satisfaction score</div></div>
            </div>
          </div>
        </div>

        {/* Privacy assurance */}
        <div className="signout-privacy">
          <div className="signout-privacy-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div className="signout-privacy-text">
            <strong>Your privacy is our #1 priority</strong>
            <span>All data encrypted &middot; Discreet billing &middot; No one will ever know &middot; 100% judgment-free zone</span>
          </div>
        </div>

        {/* Refund policy */}
        <div className="signout-refund">
          <div className="signout-refund-badge">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
          </div>
          <div>
            <strong>Money Back Guarantee</strong>
            <p>We're confident you'll love it. If you're not satisfied and haven't used your credits, we offer a full refund.</p>
          </div>
        </div>

        {/* Reviews */}
        <div className="signout-reviews">
          {[
            { name: 'James R.', text: 'Honestly surprised how good this is. The conversations feel real and the photos are incredible.' },
            { name: 'Ryan K.', text: 'The privacy is what sold me. Transactions are discreet, and the experience is top tier.' },
            { name: 'Tyler S.', text: 'Created my dream girl in 2 minutes. She remembers everything. Worth every credit.' },
          ].map((r, i) => (
            <div key={i} className="signout-review">
              <div className="signout-review-stars">
                {[...Array(5)].map((_, j) => (
                  <svg key={j} width="12" height="12" viewBox="0 0 24 24" fill="#facc15" stroke="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <p>"{r.text}"</p>
              <span>— {r.name}</span>
            </div>
          ))}
        </div>

        <div className="signout-actions">
          <button className="btn-primary btn-lg" onClick={onStay} style={{ flex: 2 }}>
            Stay &amp; keep chatting
          </button>
          <button className="btn-ghost" onClick={onLeave} style={{ flex: 1, opacity: 0.6 }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
